import BrandIcon from "./BrandIcon";
import "./ClassSetupWizard.css";
import { useEffect, useRef, useState } from "react";
import ClassNameInput from "./ClassNameInput";
import { useTranslation } from "./i18n";
import { type ClassSetupDraft, normalizeClassName, teachingSetupIssue } from "./lib/classSetup";
import { type ClassSetupProgress, parseClassSetupProgress } from "./lib/classSetupProgress";

export default function ClassSetupWizard({
  onComplete,
  onCancel,
  initialDraft,
  draftStorageKey,
  sessionControl,
}: {
  onComplete: (draft: ClassSetupDraft) => Promise<void>;
  onCancel: () => void;
  initialDraft?: ClassSetupDraft;
  draftStorageKey: string;
  sessionControl?: React.ReactNode;
}) {
  const { t } = useTranslation("classSetup");
  const [restored] = useState(() => {
    try {
      return { progress: parseClassSetupProgress(localStorage.getItem(draftStorageKey)), failed: false };
    } catch {
      return { progress: null, failed: true };
    }
  });
  const [step, setStep] = useState(restored.progress?.step ?? 0);
  const [hasHome, setHasHome] = useState(
    restored.progress?.hasHome ?? (initialDraft ? Boolean(initialDraft.homeroom) : true),
  );
  const [homeroom, setHomeroom] = useState(restored.progress?.homeroom ?? initialDraft?.homeroom ?? "");
  const [teaching, setTeaching] = useState(() =>
    (
      restored.progress?.teaching ??
      (initialDraft?.teaching.length ? initialDraft.teaching : [{ className: "", subject: "" }])
    ).map((row, index) => ({ ...row, id: index + 1 })),
  );
  const [noTeaching, setNoTeaching] = useState(
    restored.progress?.noTeaching ?? (Boolean(initialDraft?.homeroom) && initialDraft?.teaching.length === 0),
  );
  const [draftSaveFailed, setDraftSaveFailed] = useState(restored.failed);
  const [draftWritten, setDraftWritten] = useState(false);
  const draftClosed = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const nextRowId = useRef(teaching.length + 1);
  const heading = useRef<HTMLHeadingElement>(null);
  const previousStep = useRef(step);
  useEffect(() => {
    if (draftClosed.current) return;
    const progress: ClassSetupProgress = {
      version: 1,
      step,
      hasHome,
      homeroom,
      noTeaching,
      teaching: teaching.map(({ className, subject }) => ({ className, subject })),
    };
    try {
      localStorage.setItem(draftStorageKey, JSON.stringify(progress));
      setDraftSaveFailed(false);
      setDraftWritten(true);
    } catch {
      setDraftSaveFailed(true);
    }
  }, [draftStorageKey, step, hasHome, homeroom, teaching, noTeaching]);

  function clearDraft() {
    draftClosed.current = true;
    try {
      localStorage.removeItem(draftStorageKey);
    } catch {
      // Submitted classes are authoritative even when browser draft storage is unavailable.
    }
  }
  const draft: ClassSetupDraft = {
    homeroom: hasHome ? normalizeClassName(homeroom) : "",
    teaching: noTeaching ? [] : teaching,
  };
  const rows = draft.teaching.filter((row) => row.className.trim() && row.subject.trim());
  const stepNames = [t("steps.0"), t("steps.1"), t("steps.2")];
  useEffect(() => {
    if (previousStep.current !== step) heading.current?.focus({ preventScroll: true });
    previousStep.current = step;
  }, [step]);

  async function finish() {
    if (saving) return;
    const issue = teachingSetupIssue(draft, noTeaching);
    if (issue) {
      setError(t(`errors.${issue}`));
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onComplete(draft);
      clearDraft();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      if (message === "existingHomeroom" || message === "ambiguous") setError(t(`errors.${message}`));
      else setError(message || t("saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  function moveBack() {
    setError("");
    setStep((current) => current - 1);
  }
  function next() {
    if (step === 0 && hasHome && !homeroom.trim()) {
      setError(t("errorHome"));
      return;
    }
    if (step === 1) {
      const issue = teachingSetupIssue(draft, noTeaching);
      if (issue) {
        setError(t(`errors.${issue}`));
        return;
      }
    }
    setError("");
    setStep((current) => current + 1);
  }
  function updateRow(id: number, field: "className" | "subject", value: string) {
    setTeaching((current) => current.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    setError("");
  }

  return (
    <main className="class-setup-page">
      <div className="class-setup-shell">
        <header className="class-setup-brand">
          <BrandIcon className="class-setup-brand-mark" />
          <div>
            <strong>{t("title")}</strong>
            <small>{t("trial")}</small>
          </div>
          {sessionControl}
          <button
            type="button"
            onClick={() => {
              clearDraft();
              onCancel();
            }}
            disabled={saving}
          >
            {t("manualSetup")}
          </button>
        </header>
        <div className="class-setup-body">
          <aside className="class-setup-sidebar">
            <p className="class-setup-intro">{t("intro")}</p>
            <ol className="class-setup-progress" aria-label={t("title")}>
              {stepNames.map((name, index) => (
                <li
                  key={name}
                  aria-current={step === index ? "step" : undefined}
                  className={index <= step ? "reached" : ""}
                >
                  <span aria-hidden="true">{index < step ? "✓" : index + 1}</span>
                  <strong>{name}</strong>
                </li>
              ))}
            </ol>
          </aside>
          <section className="class-setup-content" aria-labelledby="class-setup-step-title">
            <small className="class-setup-step-count">{t("stepCount", { step: step + 1 })}</small>
            <h1 id="class-setup-step-title" ref={heading} tabIndex={-1}>
              {step === 0 ? t("homeTitle") : step === 1 ? t("teachingTitle") : t("reviewTitle")}
            </h1>
            <p className="class-setup-description">
              {step === 0 ? t("homeIntro") : step === 1 ? t("teachingIntro") : t("reviewIntro")}
            </p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (step === 2) void finish();
                else next();
              }}
              aria-busy={saving}
            >
              <fieldset className="class-setup-fields" disabled={saving}>
                {step === 0 && (
                  <div className="class-setup-home-fields">
                    <label className={`class-setup-choice ${hasHome ? "selected" : ""}`}>
                      <input
                        type="radio"
                        name="homeroom-role"
                        checked={hasHome}
                        onChange={() => {
                          setHasHome(true);
                          setError("");
                        }}
                      />
                      <span>{t("hasHome")}</span>
                    </label>
                    {hasHome && (
                      <label className="class-setup-home-input" htmlFor="class-setup-homeroom">
                        {t("homeLabel")}
                        <ClassNameInput
                          id="class-setup-homeroom"
                          value={homeroom}
                          maxLength={30}
                          placeholder={t("homePlaceholder")}
                          autoComplete="off"
                          onValueChange={(value) => {
                            setHomeroom(value);
                            setError("");
                          }}
                        />
                      </label>
                    )}
                    <label className={`class-setup-choice ${!hasHome ? "selected" : ""}`}>
                      <input
                        type="radio"
                        name="homeroom-role"
                        checked={!hasHome}
                        onChange={() => {
                          setHasHome(false);
                          setError("");
                        }}
                      />
                      <span>{t("noHome")}</span>
                    </label>
                  </div>
                )}
                {step === 1 && (
                  <div className="class-setup-teaching-fields">
                    {!noTeaching && (
                      <>
                        <div className="class-setup-row-labels" aria-hidden="true">
                          <span>{t("classLabel")}</span>
                          <span>{t("subjectLabel")}</span>
                          <span />
                        </div>
                        {teaching.map((row, index) => (
                          <div className="class-setup-teaching-row" key={row.id}>
                            <ClassNameInput
                              aria-label={t("rowClass", { row: index + 1 })}
                              value={row.className}
                              placeholder={t("classPlaceholder")}
                              list="setup-class-options"
                              maxLength={30}
                              autoComplete="off"
                              onValueChange={(value) => updateRow(row.id, "className", value)}
                            />
                            <input
                              aria-label={t("rowSubject", { row: index + 1 })}
                              value={row.subject}
                              placeholder={t("subjectPlaceholder")}
                              maxLength={40}
                              autoComplete="off"
                              onChange={(event) => updateRow(row.id, "subject", event.target.value)}
                            />
                            <button
                              type="button"
                              aria-label={t("rowRemove", { row: index + 1 })}
                              onClick={() => {
                                setTeaching((current) => current.filter((item) => item.id !== row.id));
                                setError("");
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <datalist id="setup-class-options">
                          {Array.from(
                            new Set([draft.homeroom, ...teaching.map((row) => normalizeClassName(row.className))]),
                          )
                            .filter(Boolean)
                            .map((name) => (
                              <option key={name} value={name} />
                            ))}
                        </datalist>
                        <button
                          className="class-setup-add"
                          type="button"
                          onClick={() => {
                            const rowId = nextRowId.current++;
                            setTeaching((current) => [...current, { id: rowId, className: "", subject: "" }]);
                          }}
                        >
                          {t("addTeaching")}
                        </button>
                      </>
                    )}
                    <label className="class-setup-no-teaching">
                      <input
                        type="checkbox"
                        checked={noTeaching}
                        onChange={(event) => {
                          setNoTeaching(event.target.checked);
                          setError("");
                        }}
                      />
                      {t("noTeaching")}
                    </label>
                    <p className="class-setup-note">{t("sharedNote")}</p>
                  </div>
                )}
                {step === 2 && (
                  <div className="class-setup-review">
                    <section aria-label={t("homeroomGroup")}>
                      <h2>{t("homeroomGroup")}</h2>
                      {draft.homeroom ? (
                        <div className="class-setup-preview-card home">
                          <strong>{draft.homeroom}</strong>
                          <small>{t("homeTools")}</small>
                        </div>
                      ) : (
                        <p>{t("noHomeroom")}</p>
                      )}
                    </section>
                    <section aria-label={t("teachingGroup")}>
                      <h2>{t("teachingGroup")}</h2>
                      <div className="class-setup-preview-grid">
                        {rows.map((row) => (
                          <div className="class-setup-preview-card" key={`${row.className}-${row.subject}`}>
                            <strong>
                              {normalizeClassName(row.className)} <span>{row.subject.trim()}</span>
                            </strong>
                            <small>{t("teachingTools")}</small>
                          </div>
                        ))}
                      </div>
                      {!rows.length && <p>{t("noTeachingSummary")}</p>}
                    </section>
                    <div className="class-setup-next-task">
                      <strong>{t("nextSetup")}</strong>
                      <p>{t("nextSetupHint")}</p>
                    </div>
                  </div>
                )}
                {error && (
                  <p className="class-setup-error" role="alert">
                    {error}
                  </p>
                )}
                <footer className="class-setup-actions">
                  <span className="class-setup-draft-status" role="status">
                    {draftSaveFailed ? t("draftSaveFailed") : draftWritten ? t("draftSaved") : t("draftSaving")}
                  </span>
                  {step > 0 && (
                    <button type="button" onClick={moveBack}>
                      {t("back")}
                    </button>
                  )}
                  <button type="submit" className={step === 2 ? "primary-action" : undefined}>
                    {saving ? t("saving") : step === 2 ? t("finish") : t("next")} <span aria-hidden="true">→</span>
                  </button>
                </footer>
              </fieldset>
            </form>
          </section>
        </div>
      </div>
    </main>
  );
}
